'use client'

import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {BlogContent} from '@/components/blog/detail/BlogContent'
import {BlogHero} from '@/components/blog/detail/BlogHero'
import {BlogNavigation} from '@/components/blog/detail/BlogNavigation'
import {BrandGrowthBanner} from '@/components/common/banner/BrandGrowthBanner'
import {Section} from '@/components/common/content/Section'
import {Page} from '@/components/Page'
import {getBlogContent} from '@/content/blog/blog'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {usableImage} from '@/utils/blog/helpers'
import {Blog} from '@/utils/contentful'
import {analytics} from '@/utils/tracking/analytics'

export type BlogDetailProps = {
  blog: Blog
  breadcrumbTrail: BreadcrumbStep[]
}

export const BlogDetail: FC<React.PropsWithChildren<BlogDetailProps>> = ({
  blog,
  breadcrumbTrail,
}) => {
  const {t} = useTranslation()
  const {header, getStarted} = getBlogContent()

  return (
    <>
      <Page
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Blog - Header)', {slug: blog.slug}),
        }}
      >
        <BlogHero blog={blog} breadcrumbTrail={breadcrumbTrail} />
        <Section hasPaddingTop hasPaddingBottom>
          <BlogContent
            text={blog.blogText}
            authors={blog.authors}
            hasListOfContents={blog.hasListOfContents}
            image={usableImage(blog.image) ? {src: blog.image.url, alt: blog.image.alt} : undefined}
          />
        </Section>
        {blog.industry ? (
          <Section hasPaddingTop hasPaddingBottom backgroundColor={theme.palette.background.butter}>
            <BlogNavigation industry={blog.industry} />
          </Section>
        ) : (
          <Section hasPaddingTop hasPaddingBottom backgroundColor={theme.palette.background.butter}>
            <BrandGrowthBanner
              title={t(getStarted.title)}
              buttonLinkHref={getStarted.buttonLinkHref}
              buttonLinkText={t(getStarted.buttonLinkText)}
              onClick={() => analytics.track('BrandGrowthBanner-BlogArticle')}
            />
          </Section>
        )}
      </Page>
    </>
  )
}
